/* eslint-disable react/no-unused-prop-types */
import React, { Component } from "react";
import uuid from "uuid/v4";
import PropTypes from "prop-types";
import Loading from "../elements/loading";
import classNames from "classnames";
import Pagination from "../forms/pagination";
import EmptyStateMessage from "../elements/empty-state-message";
import ConditionalWrap from "../renderless/conditional-wrap";
import PlainButton from "../elements/plain-button";

const TABLE_PIECES_PROPTYPES = {
  style: PropTypes.object,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  className: PropTypes.string,
};

export const TableRow = props => {
  const is_onclick = typeof props.onClick === "function";
  // I put the click on tr b/c I was getting invalid dom nesting
  const td_props = is_onclick ? { onClick: props.onClick, role: "button", tabIndex: 0 } : {};
  return (
    <tr className={classNames("et--flex-table--row", { "is-button": is_onclick })} {...td_props}>
      {props.children}
    </tr>
  );
};
TableRow.propTypes = { ...TABLE_PIECES_PROPTYPES, onClick: PropTypes.func };

export const TableHeaderRow = React.forwardRef((props, ref) => {
  return (
    <thead ref={ref}>
      <tr style={{ overflowY: "scroll", ...props.style }} className="et--flex-table--header-row">
        {props.children}
      </tr>
    </thead>
  );
});
TableHeaderRow.propTypes = TABLE_PIECES_PROPTYPES;

export const TableCell = props => {
  return (
    <td style={props.style} className={classNames("et--flex-table--cell", props.className)}>
      {props.children}
    </td>
  );
};
TableCell.propTypes = TABLE_PIECES_PROPTYPES;
export const TableBody = React.forwardRef((props, ref) => {
  const style = props.loading ? { minHeight: "10vh" } : {};
  return (
    <tbody
      ref={ref}
      style={{ overflowY: "scroll", ...style, flexBasis: props.flexBasis }}
      className="et--flex-table--body"
    >
      {props.children}
    </tbody>
  );
});
TableBody.propTypes = TABLE_PIECES_PROPTYPES;

export const TableHeaderCell = props => {
  return (
    <th style={props.style} className={classNames("et--flex-table--header-cell", props.className)}>
      {props.children}
    </th>
  );
};
TableHeaderCell.propTypes = TABLE_PIECES_PROPTYPES;

class Table extends Component {
  static propTypes = {
    columns: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    emptyStateMessageText: PropTypes.string,
    autoHeightMax: PropTypes.number,
    autoHeightMin: PropTypes.number,
    disableScrollTopOnPageChange: PropTypes.bool,
    caption: PropTypes.string.isRequired,
    tableFlexBasis: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  };

  static defaultProps = {
    interval: 50,
    emptyStateMessageText: "Not available",
  };

  state = {
    scrollbar_width: 0,
    scrollLeft: null,
  };

  bodyRef = React.createRef();
  headerRowRef = React.createRef();
  requestID = null;
  intervalID = null;

  componentDidMount() {
    this.handleInterval();
    if (this.props.interval) {
      this.intervalID = setInterval(this.handleInterval, this.props.interval);
    }
  }

  componentWillUnmount() {
    // Remove and reset interval/animationFrame
    clearInterval(this.intervalID);
    window.cancelAnimationFrame(this.requestID);
    this.requestID = null;
    this.intervalID = null;
  }

  handleInterval = () => {
    // this to sync horizontal scrolling of the headers and body
    // an onScroll listener results in a lot of UI jank, so we throttle w/ requestAnimationFrame
    window.cancelAnimationFrame(this.requestID);
    this.requestID = window.requestAnimationFrame(this.handleScroll);
  };

  handleScroll = () => {
    const body_scroll = this.bodyRef.current.scrollLeft;
    const header_scroll = this.headerRowRef.current.scrollLeft;
    if (typeof body_scroll === "number" && body_scroll !== header_scroll) {
      this.headerRowRef.current.scrollLeft = body_scroll;
    }
  };

  handlePageChange = page => {
    this.props.handlePageChange(page);
    if (!this.props.disableScrollTopOnPageChange) {
      this.bodyRef.current.scrollTop = 0;
    }
  };

  render() {
    const { loading, total, limit, caption, getTrProps } = this.props;

    return (
      <React.Fragment>
        <table className="et--flex-table--wrapper">
          <caption className="et--visually-hidden">{caption}</caption>
          <TableHeaderRow ref={this.headerRowRef}>
            {this.props.columns.map(({ width, header, headerClassName }, idx) => (
              <TableHeaderCell
                className={headerClassName}
                style={{
                  minWidth: width,
                }}
                key={uuid()}
              >
                {header}
              </TableHeaderCell>
            ))}
          </TableHeaderRow>
          <TableBody loading={loading} ref={this.bodyRef} flexBasis={this.props.tableFlexBasis}>
            {loading ? (
              <tr>
                <td>
                  <Loading />
                </td>
              </tr>
            ) : !this.props.data.length ? (
              <tr>
                <td>
                  <EmptyStateMessage text={this.props.emptyStateMessageText} />
                </td>
              </tr>
            ) : (
              this.props.data.map((datum, idx) => {
                const tr_props = typeof getTrProps === "function" ? getTrProps(datum, idx) : {};
                return (
                  <TableRow key={uuid()} {...tr_props}>
                    {this.props.columns.map((col, colIdx) => {
                      const { Cell } = col;
                      return Cell ? (
                        <Cell
                          key={uuid()}
                          className={classNames("et--flex-table--cell", col.cellClassName)}
                          {...datum}
                        />
                      ) : (
                        <TableCell className={col.cellClassName} style={{ minWidth: col.width }} key={uuid()}>
                          {typeof col.accessor === "function" ? col.accessor(datum) : datum[col.accessor]}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>

          {typeof this.props.handlePageChange === "function" && total > limit && (
            <tfoot className="et--flex-table--pagination">
              <tr>
                <td className="et--flex-table--pagination-holder">
                  <Pagination
                    page={this.props.page}
                    total={Math.ceil(total / limit)}
                    onChange={this.handlePageChange}
                  />
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </React.Fragment>
    );
  }
}

export default Table;
